import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaArrowRotateLeft, FaArrowRotateRight } from 'react-icons/fa6';
import { PiFlipHorizontalFill, PiFlipVerticalFill } from 'react-icons/pi';
import { Dialog, DialogContent, DialogTitle, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * ModalCropEditor — phiên bản đơn giản (không crop, không remove-bg)
 * Props:
 *   isOpen       — boolean, hiển thị modal
 *   onClose      — () => void
 *   onApply      — (dataUrl: string) => void
 *   initialImage — string (data URL hoặc blob URL)
 */
export default function ModalCropEditor({ isOpen, onClose, onApply, initialImage = null }) {
  const [rotate, setRotate] = useState(0);
  const [flip, setFlip] = useState({ horizontal: false, vertical: false });
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  // Reset state mỗi khi modal mở với ảnh mới
  useEffect(() => {
    if (isOpen && initialImage) {
      setRotate(0);
      setFlip({ horizontal: false, vertical: false });
    }
  }, [isOpen, initialImage]);

  // Render ảnh lên preview canvas
  const renderPreview = useCallback(() => {
    if (!canvasRef.current || !imageRef.current || !imageRef.current.complete) return;

    const img = imageRef.current;
    const canvas = canvasRef.current;
    const maxSize = 600;
    const scale = Math.min(maxSize / img.naturalWidth, maxSize / img.naturalHeight, 1);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;

    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);

    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
    ctx.restore();
  }, [rotate, flip]);

  // Re-render khi state thay đổi
  useEffect(() => {
    renderPreview();
  }, [renderPreview]);

  const handleApply = () => {
    if (!canvasRef.current || !imageRef.current) return;

    // Tạo canvas output với kích thước gốc
    const img = imageRef.current;
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = img.naturalWidth;
    outputCanvas.height = img.naturalHeight;
    const ctx = outputCanvas.getContext('2d');

    ctx.save();
    ctx.translate(img.naturalWidth / 2, img.naturalHeight / 2);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
    ctx.restore();

    const dataUrl = outputCanvas.toDataURL('image/png');
    if (onApply) onApply(dataUrl);
    if (onClose) onClose();
  };

  const iconBtnStyle = {
    minWidth: 44,
    minHeight: 44,
    padding: '8px',
    borderRadius: '8px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 0 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>Xem trước ảnh</span>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {/* Hidden img element để load ảnh gốc */}
        {initialImage && (
          <img
            ref={imageRef}
            src={initialImage}
            alt="source"
            style={{ display: 'none' }}
            onLoad={renderPreview}
          />
        )}

        {/* Canvas preview */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}
          />
        </div>

        {/* Controls: Rotate + Flip */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'center',
            marginBottom: 20,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setRotate(prev => prev - 90)}
            sx={iconBtnStyle}
            title="Xoay trái"
          >
            <FaArrowRotateLeft size={18} />
          </Button>
          <Button
            variant="outlined"
            onClick={() => setRotate(prev => prev + 90)}
            sx={iconBtnStyle}
            title="Xoay phải"
          >
            <FaArrowRotateRight size={18} />
          </Button>
          <Button
            variant="outlined"
            onClick={() => setFlip(prev => ({ ...prev, horizontal: !prev.horizontal }))}
            sx={{ ...iconBtnStyle, backgroundColor: flip.horizontal ? '#dbeafe' : '#f1f5f9' }}
            title="Lật ngang"
          >
            <PiFlipHorizontalFill size={18} />
          </Button>
          <Button
            variant="outlined"
            onClick={() => setFlip(prev => ({ ...prev, vertical: !prev.vertical }))}
            sx={{ ...iconBtnStyle, backgroundColor: flip.vertical ? '#dbeafe' : '#f1f5f9' }}
            title="Lật dọc"
          >
            <PiFlipVerticalFill size={18} />
          </Button>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ minHeight: 44, borderRadius: 2, px: 3 }}
          >
            Hủy
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ minHeight: 44, borderRadius: 2, px: 3 }}
          >
            Áp dụng
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
